import { getSeasonIdFromGameID, isSeasonYearMatchUID } from '@shared/helpers';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  getGameResults, getTeamResults, setGameResultsForSeason, setTeamResultsForSeason,
} from 'app/slices/GameScheduleSlice';
import { getSelectedYear } from 'app/slices/GamesSlice';
import useSeasonDatasetRefresh from 'hooks/useSeasonDatasetRefresh';
import { useCallback } from 'react';
import { IEnhancedTeamStats, IGameStats } from '@shared/types';
import { FirebaseCollections, Stores } from 'types';

export type UseDataLoaderForGameDetails = ReturnType<typeof useDataLoaderForGameDetails>;

/**
 * Loads season-wide game box scores and the corresponding per-team game results.
 * Used by Team, ViewTeam, and useGameDetailsManager. Multiple mounted consumers share
 * requests through useSeasonDatasetRefresh rather than maintaining separate loaded flags.
 *
 * Dataset mapping:
 * - matchresults -> Stores.GameStats -> IGameStats[] -> Redux gameResults.
 * - teammatchresults -> Stores.TeamWeeklyStats -> IEnhancedTeamStats[] -> Redux teamResults.
 * These are raw game/team results; the derived weekly, cumulative, and ranking datasets
 * are loaded separately by useDataLoaderForTeamStats.
 *
 * Loading and refresh:
 * - The selected year determines the requested season; each request loads that whole season.
 * - IndexedDB supplies cached records. A missing season or newer dataset timestamp causes
 *   a Firestore fetch, followed by persistence in IndexedDB before data reaches Redux.
 * - Game and team results use their own timestamps and independent requests. An already
 *   loaded season can refresh when the backend's existing completion status advances.
 * - Repeated handled timestamps do not fetch again. A newer timestamp received during a
 *   load is retained for a subsequent refresh; render updates do not retry failed requests.
 *
 * Data flow:
 * selected year / existing SyncManager completion update
 *   -> shared refresh helper -> worker -> IndexedDB / Firestore
 *   -> season-scoped Redux replacement -> existing game and team selectors
 *
 * Each callback retains its requested season. The reducers replace only that season's
 * records and preserve other cached seasons, so a late response cannot erase results
 * needed by the newly selected season. Failed requests
 * leave the previous Redux data intact. No selected game, week, or team is changed here.
 *
 * @return {UseDataLoaderForGameDetails} An empty object, retaining the hook's existing public interface. Consumers
 * read results through Redux selectors rather than the return value.
 * @see useSeasonDatasetRefresh for initialization, worker lifecycle, and error handling.
 *
 * @example
 * useDataLoaderForGameDetails();
 * const gameStats = useAppSelector(getSelectedGameStats);
 */
const useDataLoaderForGameDetails = () => {
  const dispatch = useAppDispatch();
  const seasonID = useAppSelector(getSelectedYear);
  const games = useAppSelector(getGameResults);
  const teams = useAppSelector(getTeamResults);
  // The existing interface can return all cached seasons. Select the requested season
  // here, leaving its shared return contract intact for other consumers.
  const onGamesLoaded = useCallback((results: IGameStats[]) => {
    dispatch(setGameResultsForSeason({
      seasonID, results: results.filter((game) => getSeasonIdFromGameID(game.gameID) === seasonID),
    }));
  }, [dispatch, seasonID]);
  const onTeamsLoaded = useCallback((results: IEnhancedTeamStats[]) => {
    dispatch(setTeamResultsForSeason({
      seasonID, results: results.filter((team) => isSeasonYearMatchUID(team.uID, seasonID)),
    }));
  }, [dispatch, seasonID]);
  useSeasonDatasetRefresh({
    store: Stores.GameStats, collection: FirebaseCollections.MatchResults, seasonID,
    hasData: games.some((game) => getSeasonIdFromGameID(game.gameID) === seasonID), onLoaded: onGamesLoaded,
  });
  useSeasonDatasetRefresh({
    store: Stores.TeamWeeklyStats, collection: FirebaseCollections.TeamMatchResults, seasonID,
    hasData: teams.some((team) => isSeasonYearMatchUID(team.uID, seasonID)), onLoaded: onTeamsLoaded,
  });
  return {};
};

export default useDataLoaderForGameDetails;
